import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography } from "@mui/material";
import {
  frequencyItemsForZone,
  resolveDisplayZone,
  timeHeaderSuffix,
  type TimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";
import { zoneAbbreviation } from "utils/dateUtils";
import RunTimestamp from "./RunTimestamp";
import TypeChipList from "./TypeChipList";

/**
 * Cells shared by the integration lists - the internal monitor
 * (IntegrationsBetaTable) and the client portal list (ClientIntegrationsTable):
 * the same fact looks the same whichever page renders it.
 */

/** Integration name with its own zone as a superscript that never truncates. */
export function IntegrationNameCell({
  name,
  timezone,
}: {
  name: string;
  timezone: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}>
      <Typography
        variant="body2"
        noWrap
        sx={{ minWidth: 0, overflow: "hidden" }}
      >
        {name}
      </Typography>
      <Typography
        component="sup"
        sx={{
          flexShrink: 0,
          ml: 0.25,
          fontSize: 10,
          lineHeight: 1.2,
          color: "text.secondary",
        }}
      >
        {zoneAbbreviation(new Date().toISOString(), timezone)}
      </Typography>
    </Box>
  );
}

export function EnabledCell({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <CheckIcon sx={{ color: "secondary.main", fontSize: 20 }} />
  ) : (
    <CloseIcon sx={{ color: "text.disabled", fontSize: 20 }} />
  );
}

/** Frequency chips re-expressed in the display zone. */
export function FrequencyCell({
  frequency,
  crons,
  timezone,
  timezoneMode,
}: {
  frequency: string | null;
  crons: string[];
  timezone: string;
  timezoneMode: TimezoneDisplayMode;
}) {
  return (
    <TypeChipList
      items={
        frequencyItemsForZone(frequency, crons, timezone, timezoneMode).items
      }
    />
  );
}

/** A run / next-run timestamp in the display zone, or a dash. */
export function TimestampCell({
  iso,
  timezone,
  timezoneMode,
  color,
}: {
  iso: string | null;
  timezone: string;
  timezoneMode: TimezoneDisplayMode;
  color?: string;
}) {
  if (!iso) return <>—</>;
  return (
    <RunTimestamp
      iso={iso}
      timeZone={resolveDisplayZone(timezoneMode, timezone)}
      short
      color={color}
    />
  );
}

const TIME_COLUMN_KEYS = new Set([
  "frequency",
  "last_run_started_at",
  "next_run_at",
]);

/**
 * Column header with the zone hint on time columns ("Last Run · UTC"): fixed
 * for local / utc, generic for client mode where the cells carry it.
 */
export function timeColumnLabel(
  key: string,
  label: string,
  timezoneMode: TimezoneDisplayMode,
): string {
  const suffix = timeHeaderSuffix(timezoneMode);
  return TIME_COLUMN_KEYS.has(key) && suffix ? `${label} · ${suffix}` : label;
}
